import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { DataViewProps, isNestedVisualizationOption } from '@va/types/report-block';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useIsHovering } from '@va/util/hooks';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { ViewOptionsDropdown } from './ViewOptionsDropdown';
import { getViewIcon } from './utils';

export const DataViewButton = ({
  selectedView,
  viewOptions,
  onViewChange,
  getToggleState,
  onViewToggleChange,
  size = 'medium',
}: DataViewProps) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const isHovering = useIsHovering(btnRef);
  const [isOpen, setIsOpen] = useState(false);
  const isDropdownAvailable = useMemo(() => {
    if (!viewOptions || (viewOptions?.length <= 1 && !isNestedVisualizationOption(viewOptions?.[0])) || !onViewChange) {
      return false;
    }

    return true;
  }, [onViewChange, viewOptions]);

  const ViewIcon = useMemo(() => {
    return getViewIcon(selectedView);
  }, [selectedView]);

  const translate = useTranslate();

  if (!isDropdownAvailable) {
    return (
      <TooltipWrapper
        trigger='click'
        content={translate('all.dataVisualization.notAvailable', {
          value: translate(`all.dataViewOptions.${selectedView}`),
        })}
      >
        <div>
          <button
            data-testid={TEST_IDS.generic.visualizationOptions.toggleBtn}
            ref={btnRef}
            className={classNames(
              'shrink-0 cursor-pointer bg-white-snow hover:bg-primary rounded-full flex items-center justify-center focus:bg-black-lighter-13',
              {
                'w-12 h-12 sm:h-9 sm:w-9 sm:rounded-9px': size === 'medium',
                'w-42px h-42px': size === 'small',
              },
            )}
          >
            <ViewIcon color={isHovering ? '#FFFFFF' : window.PRIMARY_COLOR} />
          </button>
        </div>
      </TooltipWrapper>
    );
  }

  return (
    <TooltipWrapper content={translate('all.dataVisualization.showMoreOptions')}>
      <TooltipWrapper
        useDefaultStyle={false}
        arrow={false}
        interactive
        trigger='click'
        tooltipClassNames='shadow-md rounded-15'
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) btnRef.current?.focus();
          setIsOpen(open);
        }}
        content={
          <ViewOptionsDropdown
            viewOptions={viewOptions}
            selectedView={selectedView}
            onViewChange={onViewChange}
            getToggleState={getToggleState}
            onViewToggleChange={onViewToggleChange}
          />
        }
      >
        <div>
          <button
            className={classNames(
              'relative rounded-full flex items-center justify-center bg-white-snow hover:bg-primary focus:bg-black-lighter-13',
              {
                'w-12 h-12 sm:h-9 sm:w-9 sm:rounded-9px': size === 'medium',
                'w-42px h-42px': size === 'small',
              },
            )}
            ref={btnRef}
            data-testid={TEST_IDS.generic.visualizationOptions.toggleBtn}
          >
            <ViewIcon className='sm:w-15px sm:h-15px' color={isHovering ? '#FFFFFF' : window.PRIMARY_COLOR} />
            <div className='absolute bottom-1 sm:bottom-0.5 left-1/2 -translate-x-1/2'>
              <ArrowIcon
                iconClassName={classNames({
                  'w-1.5': size === 'small',
                  'w-2.5 sm:w-1.5': size === 'medium',
                })}
                color={isHovering ? '#FFFFFF' : '#969696'}
              />
            </div>
          </button>
        </div>
      </TooltipWrapper>
    </TooltipWrapper>
  );
};

const ArrowIcon = ({ color = '#969696', iconClassName }: { color?: string; iconClassName?: string }) => {
  const { context } = useTooltipContext();
  return <DropdownArrow iconClassName={iconClassName} color={color} open={context.open} />;
};
